import { ABOVE_POSITIONS, BELOW_POSITIONS, LEFT_POSITIONS, QUADS, RIGHT_POSITIONS, SIDE_POSITIONS, PLACEMENT } from './constants';
export const getDynamicPlacement = (anchorRef, tooltipRef, requestedPlacement) => {
  const anchor = (anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.getBoundingClientRect()) || {
    width: 0,
    height: 0,
    x: 0,
    y: 0
  };
  const {
    width,
    height
  } = (tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.getBoundingClientRect()) || {
    width: 0,
    height: 0
  };
  const centerX = anchor.x + anchor.width / 2;
  const centerY = anchor.y + anchor.width / 2;
  const pageX = document.body.clientWidth;
  const pageY = document.body.clientHeight;
  const willFit = {
    'above-left': (() => width < centerX && height < anchor.y)(),
    'above': (() => width / 2 < centerX && centerX + width / 2 < pageX && height < anchor.y)(),
    'above-right': (() => width + centerX < pageX && height < anchor.y)(),
    'high-right': (() => anchor.x + anchor.width + width < pageX && height < anchor.y + anchor.height / 2)(),
    'right': (() => anchor.x + anchor.width + width < pageX && centerY - height / 2 > 0 && centerY + height / 2 < pageY)(),
    'low-right': (() => anchor.x + anchor.width + width < pageX && centerY + height < pageY)(),
    'below-right': (() => width + centerX < pageX && anchor.y + anchor.height + height < pageY)(),
    'below': (() => centerX - width / 2 > 0 && centerX + width / 2 < pageX && anchor.y + anchor.height + height < pageY)(),
    'below-left': (() => centerX - width > 0 && anchor.y + anchor.height + height < pageY)(),
    'low-left': (() => centerX - width > 0 && centerY + height < pageY)(),
    'left': (() => centerX - width > 0 && centerY - height / 2 > 0 && centerY + height / 2 < pageY)(),
    'high-left': (() => centerX - width > 0 && centerY - height > 0)(),
    'none': true
  };
  if (willFit[requestedPlacement]) return requestedPlacement;
  const visited = QUADS.map(() => false);
  const start = QUADS.filter(q => q.placements.includes(requestedPlacement))[0]; // Recursively iterate through possible placements. First try to find a fit from the requested position or
  // requested position's quadrant. If no match, check for best alternative in the opposite quadrant. If
  // this side of the recursion tree all return false, then go ahead and find a fit in the quadrant adjacent from
  // requested position's adjacent quadrant (worst alt) and finally the adjacent quadrant on the other side. If
  // all fail, return the requested placement, or default to "above" if placement is not provided via prop.

  const traverse = (quad, index) => {
    if (visited[index]) return false;
    visited[index] = true;
    const matches = quad.placements.filter(p => willFit[p]);
    return matches[0] || traverse(QUADS[quad.bestAltIndex], quad.bestAltIndex) || requestedPlacement;
  };

  return traverse(start, start.index) || traverse(QUADS[start.worstAltIndex], QUADS[start.worstAltIndex].index) || requestedPlacement;
};
export const getPseudoTopPos = (arrowSize, sideArrowOffset, bestPlacement) => {
  switch (true) {
    case ABOVE_POSITIONS.includes(bestPlacement):
      return '100%';

    case BELOW_POSITIONS.includes(bestPlacement):
      return "-".concat(arrowSize * 2, "px");

    case SIDE_POSITIONS.includes(bestPlacement):
      if ([PLACEMENT.right, PLACEMENT.left].includes(bestPlacement)) {
        return "calc(50% - ".concat(arrowSize, "px)");
      } else if ([PLACEMENT.highLeft, PLACEMENT.highRight].includes(bestPlacement)) {
        return "calc(100% - ".concat(arrowSize * 2 + sideArrowOffset, "px)");
      } else {
        return "".concat(sideArrowOffset, "px");
      }

    default:
      return '';
  }
};
export const getPseudoLeftPos = (arrowSize, sideArrowOffset, bestPlacement) => {
  switch (true) {
    case PLACEMENT.aboveLeft === bestPlacement:
      return "calc(100% - ".concat(arrowSize * 2 + sideArrowOffset, "px)");

    case PLACEMENT.above === bestPlacement:
      return "calc(50% - ".concat(arrowSize, "px)");

    case PLACEMENT.aboveRight === bestPlacement:
      return "".concat(sideArrowOffset, "px");

    case RIGHT_POSITIONS.includes(bestPlacement):
      return "-".concat(arrowSize * 2, "px");

    case PLACEMENT.belowRight === bestPlacement:
      return "".concat(sideArrowOffset, "px");

    case PLACEMENT.below === bestPlacement:
      return "calc(50% - ".concat(arrowSize, "px)");

    case PLACEMENT.belowLeft === bestPlacement:
      return "calc(100% - ".concat(arrowSize * 2 + sideArrowOffset, "px)");

    case LEFT_POSITIONS.includes(bestPlacement):
      return '100%';

    default:
      return '';
  }
};
export const getPseudoStyle = (arrowSize, titleBackground, messageBackground, bestPlacement) => {
  switch (true) {
    case ABOVE_POSITIONS.includes(bestPlacement):
      return "\n                border-top: ".concat(arrowSize, "px solid ").concat(messageBackground, ";\n                border-right: ").concat(arrowSize, "px solid transparent;\n                border-bottom: ").concat(arrowSize, "px solid transparent;\n                border-left: ").concat(arrowSize, "px solid transparent;\n                clip-path: polygon(-50% 0, 50% 60%, 150% 0);\n            ");

    case RIGHT_POSITIONS.includes(bestPlacement):
      return "\n                border-top: ".concat(arrowSize, "px solid transparent;\n                border-right: ").concat(arrowSize, "px solid ").concat(messageBackground, ";\n                border-bottom: ").concat(arrowSize, "px solid transparent;\n                border-left: ").concat(arrowSize, "px solid transparent;\n                clip-path: polygon(100% -50%, 40% 50%, 100% 150%);\n            ");

    case BELOW_POSITIONS.includes(bestPlacement):
      return "\n                border-top: ".concat(arrowSize, "px solid transparent;\n                border-right: ").concat(arrowSize, "px solid transparent;\n                border-bottom: ").concat(arrowSize, "px solid ").concat(titleBackground || messageBackground, ";\n                border-left: ").concat(arrowSize, "px solid transparent;\n                clip-path: polygon(50% 50%, -50% 100%, 150% 100%);\n            ");

    case LEFT_POSITIONS.includes(bestPlacement):
      return "\n                border-top: ".concat(arrowSize, "px solid transparent;\n                border-right: ").concat(arrowSize, "px solid transparent;\n                border-bottom: ").concat(arrowSize, "px solid transparent;\n                border-left: ").concat(arrowSize, "px solid ").concat(messageBackground, ";\n                clip-path: polygon(0 -50%, 60% 50%, 0 150%);\n            ");

    default:
      return '';
  }
};
export const getTooltipTransform = (anchorRef, arrowSize, border, sideArrowOffset, offset, bestPlacement) => {
  const halfHeight = anchorRef !== null && anchorRef !== void 0 && anchorRef.clientHeight ? anchorRef.clientHeight / 2 : 0;
  const height = (anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.clientHeight) || 0;
  const halfWidth = anchorRef !== null && anchorRef !== void 0 && anchorRef.clientWidth ? anchorRef.clientWidth / 2 : 0;
  const width = (anchorRef === null || anchorRef === void 0 ? void 0 : anchorRef.clientWidth) || 0;

  switch (bestPlacement) {
    case PLACEMENT.aboveLeft:
      return "translateX(calc(-100% + ".concat(halfWidth + sideArrowOffset + arrowSize + border, "px)) translateY(calc(-100% - ").concat(height + arrowSize + offset, "px))");

    case PLACEMENT.above:
      return "translateX(calc(-50% + ".concat(halfWidth, "px)) translateY(calc(-100% - ").concat(height + arrowSize + offset, "px))");

    case PLACEMENT.aboveRight:
      return "translateX(".concat(halfWidth - sideArrowOffset - arrowSize - border, "px) translateY(calc(-100% - ").concat(height + arrowSize + offset, "px))");

    case PLACEMENT.highRight:
      return "translateX(".concat(width + arrowSize + offset, "px) translateY(calc(-100% - ").concat(halfHeight - border - (sideArrowOffset + arrowSize), "px))");

    case PLACEMENT.lowRight:
      return "translateX(".concat(width + arrowSize + offset, "px) translateY(-").concat(halfHeight + border + sideArrowOffset + arrowSize, "px)");

    case PLACEMENT.right:
      return "translateX(".concat(width + arrowSize + offset, "px) translateY(calc(-50% - ").concat(halfHeight, "px))");

    case PLACEMENT.belowRight:
      return "translateX(calc(".concat(halfWidth - sideArrowOffset - arrowSize - border, "px)) translateY(").concat(arrowSize + offset, "px)");

    case PLACEMENT.below:
      return "translateX(calc(-50% + ".concat(halfWidth, "px)) translateY(").concat(arrowSize + offset, "px)");

    case PLACEMENT.belowLeft:
      return "translateX(calc(-100% + ".concat(halfWidth + sideArrowOffset + arrowSize + border, "px)) translateY(").concat(arrowSize + offset, "px)");

    case PLACEMENT.lowLeft:
      return "translateX(calc(-100% - ".concat(arrowSize + offset, "px)) translateY(-").concat(halfHeight + sideArrowOffset + arrowSize + border, "px)");

    case PLACEMENT.left:
      return "translateX(calc(-100% - ".concat(arrowSize + offset, "px)) translateY(calc(-50% - ").concat(halfHeight, "px))");

    case PLACEMENT.highLeft:
      return "translateX(calc(-100% - ".concat(arrowSize + offset, "px)) translateY(calc(-100% - ").concat(halfHeight - (sideArrowOffset + arrowSize + border), "px))");

    default:
      return '';
  }
};